import React, {useEffect, useState, useMemo, useCallback} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    TextField,
    Typography,
    Alert,
    Snackbar,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    Autocomplete,
    Menu
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DataGrid, GridToolbar, gridFilteredSortedRowIdsSelector, useGridApiRef} from '@mui/x-data-grid';
import {addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where} from 'firebase/firestore';
import {db} from './Configuration';
import { getStudentStatusLabel, isActiveLikeStatus, STUDENT_STATUS } from './utils/studentStatus';
import {
  dedupeAttendanceBuckets,
  ATTENDANCE_PCT_GOOD_ABOVE,
  ATTENDANCE_PCT_WARN_MIN
} from './utils/attendanceMath';
import { getGenderFilter } from './utils/permissions';
import {useFirebaseData} from './contexts/FirebaseDataContext';
import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import {v4 as uuidv4} from 'uuid';
import isoWeek from 'dayjs/plugin/isoWeek';
import { isUrduText, getUrduSxProps } from './utils/urduFont';

dayjs.extend(isoWeek);

// Slot definitions
const SLOTS = {
    'monday': [
        { id: 'slot1', label: '9:00 AM - 10:00 AM', start: 9, end: 10 },
        { id: 'slot2', label: '10:00 AM - 11:00 AM', start: 10, end: 11 },
        { id: 'slot3', label: '11:00 AM - 12:00 PM', start: 11, end: 12 },
        { id: 'slot4', label: '12:00 PM - 1:00 PM', start: 12, end: 13 }
    ],
    'tuesday': [
        { id: 'slot1', label: '9:00 AM - 10:00 AM', start: 9, end: 10 },
        { id: 'slot2', label: '10:00 AM - 11:00 AM', start: 10, end: 11 },
        { id: 'slot3', label: '11:00 AM - 12:00 PM', start: 11, end: 12 },
        { id: 'slot4', label: '12:00 PM - 1:00 PM', start: 12, end: 13 }
    ],
    'wednesday': [
        { id: 'slot1', label: '9:00 AM - 10:00 AM', start: 9, end: 10 },
        { id: 'slot2', label: '10:00 AM - 11:00 AM', start: 10, end: 11 },
        { id: 'slot3', label: '11:00 AM - 12:00 PM', start: 11, end: 12 },
        { id: 'slot4', label: '12:00 PM - 1:00 PM', start: 12, end: 13 }
    ],
    'thursday': [
        { id: 'slot1', label: '9:00 AM - 10:00 AM', start: 9, end: 10 },
        { id: 'slot2', label: '10:00 AM - 11:00 AM', start: 10, end: 11 },
        { id: 'slot3', label: '11:00 AM - 12:00 PM', start: 11, end: 12 },
        { id: 'slot4', label: '12:00 PM - 1:00 PM', start: 12, end: 13 }
    ],
    'friday': [
        { id: 'slot1', label: '9:00 AM - 10:00 AM', start: 9, end: 10 },
        { id: 'slot2', label: '10:00 AM - 11:00 AM', start: 10, end: 11 },
        { id: 'slot3', label: '11:00 AM - 12:00 PM', start: 11, end: 12 }
    ]
};

const CourseSelectorWithTable = () => {
    // Get data from context
    const {
        students: contextStudents,
        courses: contextCourses,
        campuses: contextCampuses,
        attendance: contextAttendance,
        initialLoadComplete,
        refreshCollection,
        getCoursesByCampus
    } = useFirebaseData();

    // Check if user is admin
    const isAdmin = () => {
        try {
            const userType = localStorage.getItem('userType');
            return userType === 'Admin';
        } catch (error) {
            return false;
        }
    };

    const [searchParams] = useSearchParams();
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedDate, setSelectedDate] = useState(() => {
        // Check if date is provided in URL query params
        const dateParam = searchParams.get('date');
        if (dateParam) {
            // Try parsing as DD/MM/YYYY first (common format in the app)
            let parsedDate = dayjs(dateParam, 'DD/MM/YYYY', true);
            // If that fails, try YYYY-MM-DD format
            if (!parsedDate.isValid()) {
                parsedDate = dayjs(dateParam, 'YYYY-MM-DD', true);
            }
            // If that also fails, try auto-parse
            if (!parsedDate.isValid()) {
                parsedDate = dayjs(dateParam);
            }
            if (parsedDate.isValid()) {
                return parsedDate.startOf('day');
            }
        }
        return dayjs().startOf('day');
    });
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [slotAttendanceStatus, setSlotAttendanceStatus] = useState({}); // Track which slots have attendance marked
    const [slotAttendanceMap, setSlotAttendanceMap] = useState({}); // Map slot IDs to attendance records
    const [slotCourseMap, setSlotCourseMap] = useState({}); // Map slot IDs to course names (for display)
    const [tableData, setTableData] = useState([]);
    const [presentStudents, setPresentStudents] = useState([]);
    const [absentStudents, setAbsentStudents] = useState([]);
    const [leaveStudents, setLeaveStudents] = useState([]);
    const [leaveReasons, setLeaveReasons] = useState({});  // State to store leave reasons
    const [originalAttendanceData, setOriginalAttendanceData] = useState(null); // Store original data to detect changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [subjectWiseAttendance, setSubjectWiseAttendance] = useState([]);
    const [sessionData, setSessionData] = useState([]);
    const [topic, setTopic] = useState('');
    const [topicId, setTopicId] = useState('');
    const [comments, setComments] = useState('');
    const [availableTopics, setAvailableTopics] = useState([]);
    const [error, setError] = useState("");
    const [selectedCampus, setSelectedCampus] = useState(''); // Selected campus for filtering
    const [markingAttendance, setMarkingAttendance] = useState(false);
    const [deletingAttendance, setDeletingAttendance] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [studentDetailDialog, setStudentDetailDialog] = useState(false);
    const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
    const [attendanceExpanded, setAttendanceExpanded] = useState(false);
    const [loadingAttendance, setLoadingAttendance] = useState(false);
    const [allAttendanceDialog, setAllAttendanceDialog] = useState(false);
    const [allAttendanceData, setAllAttendanceData] = useState([]);
    const [loadingAllAttendance, setLoadingAllAttendance] = useState(false);
    const [selectedAttendanceRows, setSelectedAttendanceRows] = useState([]);
    const [extraAttendanceRecords, setExtraAttendanceRecords] = useState([]); // Records beyond the 4 slots
    const [dateChangeDialog, setDateChangeDialog] = useState(false);
    const [attendanceToChangeDate, setAttendanceToChangeDate] = useState(null);
    const [newDate, setNewDate] = useState(null);
    const [updatingDate, setUpdatingDate] = useState(false);
    const attendanceGridApiRef = useGridApiRef();

    // Use context data for campuses
    const campuses = contextCampuses;

    // Filter courses based on selected campus
    const courses = useMemo(() => {
        if (!selectedCampus) return [];
        return contextCourses.filter(course =>
            course.campusCodes && course.campusCodes.includes(selectedCampus)
        );
    }, [contextCourses, selectedCampus]);

    // Fetch topics from Topics collection for selected course
    const fetchTopicsForCourse = useCallback(async (courseName) => {
        if (!courseName) {
            setAvailableTopics([]);
            return;
        }

        try {
            const topicsRef = collection(db, 'Topics');
            const q = query(topicsRef, where('courseName', '==', courseName));
            const querySnapshot = await getDocs(q);
            const topicsList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                topicId: doc.data().topicId || doc.id,
                name: doc.data().name,
                courseName: doc.data().courseName
            }));
            setAvailableTopics(topicsList);
        } catch (error) {
            console.error('Error fetching topics:', error);
            setAvailableTopics([]);
        }
    }, []);

    // Helper function to check if a course has predefined topics
    const hasPredefinedTopics = useCallback((courseName) => {
        if (!courseName) return false;
        const normalizedName = courseName.trim().toLowerCase();
        return normalizedName === 'special lectures' || 
               (normalizedName.includes('fikr') && normalizedName.includes('islami'));
    }, []);

    // Update topics when course changes
    useEffect(() => {
        if (selectedCourse && hasPredefinedTopics(selectedCourse.name)) {
            fetchTopicsForCourse(selectedCourse.name);
        } else {
            setAvailableTopics([]);
            setTopicId('');
        }
    }, [selectedCourse, fetchTopicsForCourse, hasPredefinedTopics]);

    const handleCampusChange = (event) => {
        setSelectedCampus(event.target.value);
    };

    // Get available slots based on selected date
    const getAvailableSlots = useCallback(() => {
        if (!selectedDate) return [];
        const dayName = selectedDate.format('dddd').toLowerCase();
        return SLOTS[dayName] || [];
    }, [selectedDate]);

    // Check if attendance exists for a slot (backward compatible with old time formats)
    const checkSlotAttendance = useCallback(async (date, slot, course) => {
        if (!course || !date || !slot) return null;

        const dateStr = date.format('YYYY-MM-DD');
        const startTimeExact = `${dateStr}-${String(slot.start).padStart(2, '0')}:00`;
        const endTimeExact = `${dateStr}-${String(slot.end).padStart(2, '0')}:00`;

        try {
            // Use context attendance data if available for better performance
            let attendanceData = contextAttendance || [];
            
            // Debug: Log available attendance data
            if (attendanceData.length > 0) {
                const courseMatches = attendanceData.filter(a => 
                    a.courseName && a.courseName.toLowerCase().includes(course.name.toLowerCase())
                );
                console.log(`Found ${courseMatches.length} attendance records for course "${course.name}"`);
                if (courseMatches.length > 0) {
                    console.log('Sample records:', courseMatches.slice(0, 3).map(a => ({
                        courseName: a.courseName,
                        startTime: a.startTime,
                        endTime: a.endTime,
                        date: a.startTime?.split('-').slice(0, 3).join('-')
                    })));
                }
            }
            
            // Filter by course name (case-insensitive for flexibility) and campusCode
            attendanceData = attendanceData.filter(a => 
                a.courseName && a.courseName.trim().toLowerCase() === course.name.trim().toLowerCase() &&
                a.campusCode === selectedCampus
            );
            
            // Try exact match first (new format: HH:00)
            let match = attendanceData.find(a => 
                a.startTime === startTimeExact && 
                a.endTime === endTimeExact
            );
            
            if (match) {
                return {
                    exists: true,
                    data: match,
                    docId: match.id
                };
            }
            
            // If not found, check for old format (any minutes within the hour range)
            // Old format examples: 2025-04-15-09:00, 2025-04-15-09:30, 2025-04-15-10:00
            const matchingRecords = attendanceData.filter(a => {
                const startTime = a.startTime;
                const endTime = a.endTime;
                
                if (!startTime || !endTime) return false;
                
                // Parse the time (format: YYYY-MM-DD-HH:mm)
                const startMatch = startTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                const endMatch = endTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                
                if (!startMatch || !endMatch) return false;
                
                const startDate = startMatch[1];
                const startHour = parseInt(startMatch[2]);
                const startMin = parseInt(startMatch[3]);
                const endDate = endMatch[1];
                const endHour = parseInt(endMatch[2]);
                const endMin = parseInt(endMatch[3]);
                
                // Check if date matches
                if (startDate !== dateStr || endDate !== dateStr) return false;
                
                // Check if times fall within the slot range
                // Slot: 9-10 means 09:00 to 10:00
                // We accept: startHour = 9 and endHour = 10 (regardless of minutes)
                const startTimeValue = startHour * 60 + startMin; // Convert to minutes
                const endTimeValue = endHour * 60 + endMin;
                const slotStartValue = slot.start * 60; // Slot start in minutes
                const slotEndValue = slot.end * 60; // Slot end in minutes
                
                // Check if the attendance time range overlaps with the slot
                return startTimeValue >= slotStartValue && 
                       startTimeValue < slotEndValue &&
                       endTimeValue > slotStartValue && 
                       endTimeValue <= slotEndValue;
            });
            
            if (matchingRecords.length > 0) {
                // Return the first match
                const match = matchingRecords[0];
                return {
                    exists: true,
                    data: match,
                    docId: match.id
                };
            }
            
            // If still not found, try Firebase query as fallback
            const attendanceRef = collection(db, 'Attendance');
            const q = query(
                attendanceRef,
                where('courseName', '==', course.name),
                where('campusCode', '==', selectedCampus)
            );
            const querySnapshot = await getDocs(q);
            
            const matchingDocs = querySnapshot.docs.filter(doc => {
                const data = doc.data();
                const startTime = data.startTime;
                const endTime = data.endTime;
                
                if (!startTime || !endTime) return false;
                if (data.campusCode !== selectedCampus) return false;
                
                // Check exact match first
                if (startTime === startTimeExact && endTime === endTimeExact) {
                    return true;
                }
                
                // Parse and check range - more flexible matching
                const startMatch = startTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                const endMatch = endTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                
                if (!startMatch || !endMatch) return false;
                
                const startDate = startMatch[1];
                const startHour = parseInt(startMatch[2]);
                const startMin = parseInt(startMatch[3]);
                const endDate = endMatch[1];
                const endHour = parseInt(endMatch[2]);
                const endMin = parseInt(endMatch[3]);
                
                // Date must match
                if (startDate !== dateStr || endDate !== dateStr) return false;
                
                // For exact hour matches (09:00 to 10:00)
                if (startHour === slot.start && endHour === slot.end && startMin === 0 && endMin === 0) {
                    return true;
                }
                
                // For flexible matching: start hour matches slot start, end hour matches slot end
                // This handles cases like 09:00-10:00, 09:30-10:30, etc.
                if (startHour === slot.start && endHour === slot.end) {
                    return true;
                }
                
                return false;
            });
            
            if (matchingDocs.length > 0) {
                const doc = matchingDocs[0];
                const docData = doc.data();
                return {
                    exists: true,
                    data: { ...docData, id: doc.id }, // Ensure id is included
                    docId: doc.id
                };
            }
            
            return { exists: false, data: null, docId: null };
        } catch (error) {
            console.error('Error checking slot attendance:', error);
            return null;
        }
    }, [contextAttendance, selectedCampus]);

    // Check all slots for the selected date (check ALL courses, not just selected one)
    const checkAllSlotsForDate = useCallback(async () => {
        if (!selectedDate) {
            setSlotAttendanceStatus({});
            setSlotCourseMap({});
            return;
        }

        const slots = getAvailableSlots();
        const status = {};
        const courseMap = {};
        const attendanceMap = {};
        const dateStr = selectedDate.format('YYYY-MM-DD');

        try {
            // Get all attendance for this date (regardless of course or time)
            let attendanceData = contextAttendance || [];
            
            // Filter by date and campusCode (we want to see ALL courses' attendance for this day and campus)
            const dayAttendance = attendanceData.filter(a => {
                if (!a.courseName || !a.startTime) return false;
                
                // Check if campusCode matches
                if (a.campusCode !== selectedCampus) return false;
                
                // Check if date matches (extract date from startTime)
                const timeMatch = a.startTime.match(/^(\d{4}-\d{2}-\d{2})-/);
                if (!timeMatch) return false;
                
                return timeMatch[1] === dateStr;
            });

            // Sort by start time to maintain order
            dayAttendance.sort((a, b) => {
                const timeA = a.startTime || '';
                const timeB = b.startTime || '';
                return timeA.localeCompare(timeB);
            });

            const matchedIndices = new Set();

            for (let i = 0; i < slots.length; i++) {
                const slot = slots[i];
                const matchIdx = dayAttendance.findIndex((a, idx) => {
                    if (matchedIndices.has(idx)) return false;
                    const startMatch = a.startTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                    if (!startMatch) return false;
                    const startHour = parseInt(startMatch[2]);
                    return startHour === slot.start;
                });

                if (matchIdx !== -1) {
                    matchedIndices.add(matchIdx);
                    const attendance = dayAttendance[matchIdx];
                    status[slot.id] = true;
                    attendanceMap[slot.id] = attendance;
                    courseMap[slot.id] = attendance.courseName;
                } else {
                    status[slot.id] = false;
                }
            }

            const extraRecords = dayAttendance.filter((_, idx) => !matchedIndices.has(idx));
            setExtraAttendanceRecords(extraRecords);

            // Store the mapping for later use when selecting a slot
            setSlotAttendanceStatus(status);
            setSlotAttendanceMap(attendanceMap);
            setSlotCourseMap(courseMap);
            
        } catch (error) {
            console.error('Error checking slots:', error);
            // Set all to false on error
            slots.forEach(slot => {
                status[slot.id] = false;
            });
            setSlotAttendanceStatus(status);
            setSlotAttendanceMap({});
            setSlotCourseMap({});
            setExtraAttendanceRecords([]);
        }
    }, [selectedDate, getAvailableSlots, contextAttendance, selectedCampus]);

    // Function to change date of an attendance record
    const handleChangeDate = (attendance) => {
        setAttendanceToChangeDate(attendance);
        // Parse current date from startTime
        const timeMatch = attendance.startTime.match(/^(\d{4}-\d{2}-\d{2})-/);
        if (timeMatch) {
            setNewDate(dayjs(timeMatch[1]));
        } else {
            setNewDate(dayjs());
        }
        setDateChangeDialog(true);
    };

    // Helper function to check if date change is allowed
    const checkDateChangeAllowed = useCallback((date) => {
        // Check if date is Monday-Friday (slots are only available Mon-Fri)
        const dayName = date.format('dddd').toLowerCase();
        const availableSlots = SLOTS[dayName];
        
        if (!availableSlots || availableSlots.length === 0) {
            return { allowed: false, reason: `No slots available for ${dayName}. Please select Monday to Friday.` };
        }

        // Admins can change date even if max slots are filled
        const admin = isAdmin();
        if (admin) {
            return { allowed: true };
        }

        // For non-admins, check if the new date already has maximum slots filled
        const dateStr = date.format('YYYY-MM-DD');
        const existingRecords = contextAttendance.filter(a => {
            if (!a.startTime) return false;
            if (a.id === attendanceToChangeDate?.id) return false; // Exclude current record
            if (a.campusCode !== selectedCampus) return false; // Filter by campus
            
            // Check if date matches
            const timeMatch = a.startTime.match(/^(\d{4}-\d{2}-\d{2})-/);
            return timeMatch && timeMatch[1] === dateStr;
        });

        const maxSlots = availableSlots.length; // 4 for Mon-Thu, 3 for Friday
        if (existingRecords.length >= maxSlots) {
            return { 
                allowed: false, 
                reason: `Date ${date.format('DD/MM/YYYY')} already has ${maxSlots} attendance record(s) (maximum slots for ${dayName}). Please select a different date or remove an existing record first.` 
            };
        }

        return { allowed: true };
    }, [contextAttendance, attendanceToChangeDate, selectedCampus]);

    const handleUpdateDate = async () => {
        if (!attendanceToChangeDate || !newDate) {
            showSnackbar('Please select a new date.', 'warning');
            return;
        }

        // Check if new date is in the future
        if (newDate.isAfter(dayjs(), 'day')) {
            showSnackbar('Cannot set date to future. Please select today or a past date.', 'warning');
            return;
        }

        setUpdatingDate(true);
        try {
            const docId = attendanceToChangeDate.id;
            if (!docId) {
                showSnackbar('Error: Attendance record ID not found.', 'error');
                return;
            }

            // Parse the time from original startTime (format: YYYY-MM-DD-HH:mm)
            const timeMatch = attendanceToChangeDate.startTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
            if (!timeMatch) {
                showSnackbar('Error: Could not parse time from attendance record.', 'error');
                return;
            }

            const hour = timeMatch[2];
            const minute = timeMatch[3];
            
            // Parse endTime similarly
            const endTimeMatch = attendanceToChangeDate.endTime.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
            if (!endTimeMatch) {
                showSnackbar('Error: Could not parse end time from attendance record.', 'error');
                return;
            }
            const endHour = endTimeMatch[2];
            const endMinute = endTimeMatch[3];

            // Check if date change is allowed (only check if date has max slots, not time validation)
            const dateCheck = checkDateChangeAllowed(newDate);
            if (!dateCheck.allowed) {
                showSnackbar(dateCheck.reason, 'error');
                return;
            }

            const newDateStr = newDate.format('YYYY-MM-DD');
            
            // Update startTime and endTime with new date
            // Use :00 for minutes to match slot format
            const newStartTime = `${newDateStr}-${hour}:00`;
            const newEndTime = `${newDateStr}-${endHour}:00`;

            // Update in Firebase
            const attendanceRef = doc(db, 'Attendance', docId);
            await updateDoc(attendanceRef, {
                startTime: newStartTime,
                endTime: newEndTime
            });

            showSnackbar(`Date updated successfully to ${newDate.format('DD/MM/YYYY')}`, 'success');
            
            // Refresh attendance data
            await refreshCollection('attendance');
            await checkAllSlotsForDate();
            
            // Close dialog
            setDateChangeDialog(false);
            setAttendanceToChangeDate(null);
            setNewDate(null);
        } catch (error) {
            console.error('Error updating date:', error);
            showSnackbar('Error updating date. Please try again.', 'error');
        } finally {
            setUpdatingDate(false);
        }
    };

    // Auto-select campus if only one available
    useEffect(() => {
        if (initialLoadComplete && campuses.length === 1 && !selectedCampus) {
            setSelectedCampus(campuses[0].campusCode);
        }
    }, [initialLoadComplete, campuses, selectedCampus]);

    // Check slots when date changes (check ALL courses, not just selected one)
    useEffect(() => {
        if (selectedDate) {
            checkAllSlotsForDate();
        }
    }, [selectedDate, checkAllSlotsForDate]);

    // Update table data when campus selection changes (using context data)
    useEffect(() => {
        if (!initialLoadComplete) return;

        if (!selectedCampus) {
            // Clear data when no campus selected
            setTableData([]);
            setSelectedCourse(null);
            setPresentStudents([]);
            setAbsentStudents([]);
            setLeaveStudents([]);
            setLeaveReasons({});
            setSelectedStudent(null);
            setSubjectWiseAttendance([]);
            setSessionData([]);
            setTopic('');
            setTopicId('');
            setComments('');
        } else {
            // Filter students from context data
            let studentList = contextStudents.filter(student =>
                student.campusCode === selectedCampus
            );
            const genderFilter = getGenderFilter();
            if (genderFilter) {
                studentList = studentList.filter(s =>
                    (s.gender || '').toLowerCase() === genderFilter.toLowerCase()
                );
            }
            studentList = studentList.map(student => {
                const campus = campuses.find(c => c.campusCode === student.campusCode);
                return {
                    ...student,
                    campusName: campus ? campus.campusName : student.campusCode
                };
            });

            // Sort same as Student Management: Male first, then by ID (numeric-aware)
            studentList.sort((a, b) => {
                const aMale = (a.gender || '').toString().toLowerCase() === 'male';
                const bMale = (b.gender || '').toString().toLowerCase() === 'male';
                if (aMale && !bMale) return -1;
                if (!aMale && bMale) return 1;
                return (a.id || '').toString().localeCompare((b.id || '').toString(), undefined, { numeric: true });
            });
            setTableData(studentList);
        }
    }, [selectedCampus, initialLoadComplete, contextStudents, campuses]);

    const handleCourseSelect = async (course) => {
        // Simply change the course - user can save when ready
        setSelectedCourse(course);
        
        // If slot is selected, check if there's attendance for the new course
        // If not, keep the current data so user can change course and save
        if (selectedSlot) {
            const result = await checkSlotAttendance(selectedDate, selectedSlot, course);
            if (result?.exists && result.data) {
                // Load existing attendance for this course
                setPresentStudents(result.data.presentStudents || []);
                setAbsentStudents(result.data.absentStudents || []);
                setLeaveStudents(result.data.leaveStudents || []);
                setLeaveReasons(result.data.leaveReasons || {});
                setTopic(result.data.topic || '');
                setTopicId(result.data.topicId || '');
                setComments(result.data.comments || '');
                setOriginalAttendanceData({
                    courseName: course.name,
                    presentStudents: [...(result.data.presentStudents || [])],
                    absentStudents: [...(result.data.absentStudents || [])],
                    leaveStudents: [...(result.data.leaveStudents || [])],
                    leaveReasons: {...(result.data.leaveReasons || {})},
                    topic: result.data.topic || '',
                    topicId: result.data.topicId || '',
                    comments: result.data.comments || ''
                });
                setHasUnsavedChanges(false);
            } else {
                // No attendance for this course - keep current data so user can change course and save
                // Don't clear the data, just mark that course has changed
                if (originalAttendanceData && originalAttendanceData.courseName !== course.name) {
                    // Course changed, mark as unsaved
                    setHasUnsavedChanges(true);
                }
            }
        }
    };

    const handleDateChange = async (newDate) => {
        setSelectedDate(newDate);
        setSelectedSlot(null);
        setPresentStudents([]);
        setAbsentStudents([]);
        setLeaveStudents([]);
        setLeaveReasons({});
        setTopic('');
        setComments('');
        setSlotAttendanceMap({}); // Clear mapped attendance
        setSlotCourseMap({}); // Clear course map
        
        // Check slots for all courses on this date
        await checkAllSlotsForDate();
    };

    const handleSlotSelect = async (slot) => {
        if (!selectedDate) return;

        // Check for unsaved changes ONLY when switching to a DIFFERENT slot
        if (hasUnsavedChanges && selectedSlot && selectedSlot.id !== slot.id) {
            const shouldProceed = window.confirm(
                'You have unsaved changes. Do you want to discard them and continue?'
            );
            if (!shouldProceed) {
                return; // Don't switch slots
            }
            // User chose to discard, reset the flag
            setHasUnsavedChanges(false);
        }

        // First check if we have mapped attendance for this slot (simpler approach for old data)
        const mappedAttendance = slotAttendanceMap[slot.id];
        const slotCourseName = slotCourseMap[slot.id];
        
        if (mappedAttendance && slotCourseName) {
            // Auto-select the course that has attendance in this slot
            const matchingCourse = contextCourses.find(c => 
                c.name.trim().toLowerCase() === slotCourseName.trim().toLowerCase()
            );
            
            if (matchingCourse) {
                // Auto-select the course and load attendance data
                setSelectedCourse(matchingCourse);
                setSelectedSlot(slot);
                setPresentStudents(mappedAttendance.presentStudents || []);
                setAbsentStudents(mappedAttendance.absentStudents || []);
                setLeaveStudents(mappedAttendance.leaveStudents || []);
                setLeaveReasons(mappedAttendance.leaveReasons || {});
                setTopic(mappedAttendance.topic || '');
                setTopicId(mappedAttendance.topicId || '');
                setComments(mappedAttendance.comments || '');
                // Store original data for change detection
                setOriginalAttendanceData({
                    courseName: matchingCourse.name,
                    presentStudents: [...(mappedAttendance.presentStudents || [])],
                    absentStudents: [...(mappedAttendance.absentStudents || [])],
                    leaveStudents: [...(mappedAttendance.leaveStudents || [])],
                    leaveReasons: {...(mappedAttendance.leaveReasons || {})},
                    topic: mappedAttendance.topic || '',
                    topicId: mappedAttendance.topicId || '',
                    comments: mappedAttendance.comments || ''
                });
                setHasUnsavedChanges(false);
                return;
            }
        }

        // If no mapped attendance found, try exact time match (for new format)
        // If course is already selected, use it; otherwise, user needs to select one
        if (selectedCourse) {
            setSelectedSlot(slot);
            const result = await checkSlotAttendance(selectedDate, slot, selectedCourse);
            
            if (result?.exists && result.data) {
                // Load existing attendance data
                setPresentStudents(result.data.presentStudents || []);
                setAbsentStudents(result.data.absentStudents || []);
                setLeaveStudents(result.data.leaveStudents || []);
                setLeaveReasons(result.data.leaveReasons || {});
                setTopic(result.data.topic || '');
                setTopicId(result.data.topicId || '');
                setComments(result.data.comments || '');
                // Store original data
                setOriginalAttendanceData({
                    courseName: selectedCourse.name,
                    presentStudents: [...(result.data.presentStudents || [])],
                    absentStudents: [...(result.data.absentStudents || [])],
                    leaveStudents: [...(result.data.leaveStudents || [])],
                    leaveReasons: {...(result.data.leaveReasons || {})},
                    topic: result.data.topic || '',
                    topicId: result.data.topicId || '',
                    comments: result.data.comments || ''
                });
            } else {
                // Clear for new attendance
                // setPresentStudents([]);
                // setAbsentStudents([]);
                // setLeaveStudents([]);
                // setLeaveReasons({});
                setTopic('');
                setComments('');
                setOriginalAttendanceData(null);
            }
            setHasUnsavedChanges(false);
        } else {
            // No course selected and no mapped attendance - allow slot selection but show message
            setSelectedSlot(slot);
            setPresentStudents([]);
            setAbsentStudents([]);
            setLeaveStudents([]);
            setLeaveReasons({});
            setTopic('');
            setTopicId('');
            setComments('');
            setOriginalAttendanceData(null);
            setHasUnsavedChanges(false);
            showSnackbar('Please select a course to mark attendance for this slot.', 'info');
        }
    };

    const deleteAttendance = async () => {
        if (!selectedCourse || !selectedDate || !selectedSlot) {
            showSnackbar('Please select a course, date, and slot.', 'error');
            return;
        }

        const courseName = selectedCourse.name;
        if (!window.confirm(`Are you sure you want to delete attendance for "${courseName}" - ${selectedSlot.label}?`)) {
            return;
        }

        setDeletingAttendance(true);
        try {
            // First check mapped attendance (for old data - simpler approach)
            const mappedAttendance = slotAttendanceMap[selectedSlot.id];
            let docIdToDelete = null;
            
            if (mappedAttendance && mappedAttendance.id) {
                docIdToDelete = mappedAttendance.id;
            } else {
                // Try to find by exact time match (for new format)
                const result = await checkSlotAttendance(selectedDate, selectedSlot, selectedCourse);
                if (result?.exists && result.docId) {
                    docIdToDelete = result.docId;
                }
            }
            
            if (docIdToDelete) {
                await deleteDoc(doc(db, 'Attendance', docIdToDelete));
                showSnackbar(`Attendance for "${courseName}" deleted successfully`, 'success');
                
                // Clear the slot and refresh
                setPresentStudents([]);
                setAbsentStudents([]);
                setLeaveStudents([]);
                setLeaveReasons({});
                setTopic('');
                setComments('');
                setSelectedSlot(null);
                
                // Update slot status
                await refreshCollection('attendance');
                await checkAllSlotsForDate();
            } else {
                showSnackbar('No attendance found for this slot.', 'info');
            }
        } catch (error) {
            console.error('Error deleting attendance:', error);
            showSnackbar('Error deleting attendance. Please try again.', 'error');
        } finally {
            setDeletingAttendance(false);
        }
    };

    const handlePresentToggle = (studentId) => {
        setPresentStudents(presentStudents.includes(studentId)
            ? presentStudents.filter(id => id !== studentId)
            : [...presentStudents, studentId]);
        setAbsentStudents(absentStudents.filter(id => id !== studentId));
        setLeaveStudents(leaveStudents.filter(id => id !== studentId));
    };

    const handleAbsentToggle = (studentId) => {
        setAbsentStudents(absentStudents.includes(studentId)
            ? absentStudents.filter(id => id !== studentId)
            : [...absentStudents, studentId]);
        setPresentStudents(presentStudents.filter(id => id !== studentId));
        setLeaveStudents(leaveStudents.filter(id => id !== studentId));
    };

    const handleLeaveToggle = (studentId) => {
        setLeaveStudents(leaveStudents.includes(studentId)
            ? leaveStudents.filter(id => id !== studentId)
            : [...leaveStudents, studentId]);
        setPresentStudents(presentStudents.filter(id => id !== studentId));
        setAbsentStudents(absentStudents.filter(id => id !== studentId));
    };

    const handleLeaveReasonChange = (studentId, reason) => {
        setLeaveReasons({...leaveReasons, [studentId]: reason});
    };

    // Check if data has changed from original
    const checkForChanges = useCallback(() => {
        // Only check if we have a slot selected
        if (!selectedSlot) {
            setHasUnsavedChanges(false);
            return;
        }

        if (!originalAttendanceData) {
            // New attendance - check if there's any data entered
            const hasData = presentStudents.length > 0 || absentStudents.length > 0 || leaveStudents.length > 0 || topic || comments;
            setHasUnsavedChanges(hasData);
            return;
        }

        if (!selectedCourse) {
            setHasUnsavedChanges(false);
            return;
        }

        // Compare with original data - only mark as changed if there are actual differences
        const presentChanged = JSON.stringify([...presentStudents].sort()) !== JSON.stringify([...originalAttendanceData.presentStudents].sort());
        const absentChanged = JSON.stringify([...absentStudents].sort()) !== JSON.stringify([...originalAttendanceData.absentStudents].sort());
        const leaveChanged = JSON.stringify([...leaveStudents].sort()) !== JSON.stringify([...originalAttendanceData.leaveStudents].sort());
        const topicChanged = (topic || '') !== (originalAttendanceData.topic || '');
        const commentsChanged = (comments || '') !== (originalAttendanceData.comments || '');
        const courseChanged = selectedCourse.name !== originalAttendanceData.courseName;
        
        // Compare leave reasons
        const originalReasons = originalAttendanceData.leaveReasons || {};
        const currentReasons = leaveReasons || {};
        const leaveReasonsChanged = JSON.stringify(currentReasons) !== JSON.stringify(originalReasons);

        const hasChanges = presentChanged || absentChanged || leaveChanged || topicChanged || commentsChanged || courseChanged || leaveReasonsChanged;
        setHasUnsavedChanges(hasChanges);
    }, [presentStudents, absentStudents, leaveStudents, topic, comments, leaveReasons, selectedCourse, originalAttendanceData, selectedSlot]);

    // Check for changes whenever data changes
    useEffect(() => {
        if (selectedSlot) {
            checkForChanges();
        }
    }, [checkForChanges, selectedSlot]);

    const getFilteredStudentIds = useCallback(() => {
        if (attendanceGridApiRef.current?.state) {
            try {
                return gridFilteredSortedRowIdsSelector(attendanceGridApiRef).map(id => String(id));
            } catch (error) {
                console.warn('Could not read filtered attendance rows, falling back to all students.', error);
            }
        }

        return tableData.map(student => student.id);
    }, [attendanceGridApiRef, tableData]);

    const markAttendance = async () => {
        if (!selectedCourse || !selectedDate || !selectedSlot) {
            showSnackbar('Please select a course, date, and slot.', 'error');
            return;
        }

        if (presentStudents.length === 0 && absentStudents.length === 0 && leaveStudents.length === 0) {
            showSnackbar('Please mark attendance for at least one student.', 'warning');
            return;
        }

        setMarkingAttendance(true);
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const startTime = `${dateStr}-${String(selectedSlot.start).padStart(2, '0')}:00`;
            const endTime = `${dateStr}-${String(selectedSlot.end).padStart(2, '0')}:00`;

            const attendanceRef = collection(db, 'Attendance');
            
            // First check if we have mapped attendance (old data)
            const mappedAttendance = slotAttendanceMap[selectedSlot.id];
            let docIdToUpdate = null;
            
            if (mappedAttendance && mappedAttendance.id) {
                docIdToUpdate = mappedAttendance.id;
            } else {
                // Try exact time match (new format)
                const q = query(
                    attendanceRef,
                    where('courseName', '==', selectedCourse.name),
                    where('startTime', '==', startTime),
                    where('endTime', '==', endTime),
                    where('campusCode', '==', selectedCampus)
                );
                const querySnapshot = await getDocs(q);
                
                if (!querySnapshot.empty) {
                    docIdToUpdate = querySnapshot.docs[0].id;
                }
            }

            if (docIdToUpdate) {
                // Update existing attendance
                const attendanceDocRef = doc(db, 'Attendance', docIdToUpdate);
                
                // Get the original course name from mapped attendance or by fetching the document
                let originalCourseName = null;
                if (mappedAttendance && mappedAttendance.courseName) {
                    originalCourseName = mappedAttendance.courseName;
                } else {
                    // Fetch the document directly to get original course name
                    try {
                        const docRef = doc(db, 'Attendance', docIdToUpdate);
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                            originalCourseName = docSnap.data().courseName;
                        }
                    } catch (e) {
                        console.log('Could not fetch original course name:', e);
                    }
                }
                
                const courseChanged = originalCourseName && originalCourseName !== selectedCourse.name;

                // Attendance.courseName is always Courses.name (see src/utils/courseAttendance.js)
                await updateDoc(attendanceDocRef, {
                    courseName: selectedCourse.name, // Update course name if changed
                    courseId: selectedCourse.id,
                    presentStudents,
                    absentStudents,
                    leaveStudents,
                    startTime: startTime, // Update to new format
                    endTime: endTime, // Update to new format
                    tableId: uuidv4(),
                    topic,
                    topicId: topicId || null,
                    comments,
                    leaveReasons,
                    campusCode: selectedCampus
                });

                if (courseChanged) {
                    showSnackbar(`Attendance updated successfully. Course changed from "${originalCourseName}" to "${selectedCourse.name}"`, 'success');
                } else {
                    showSnackbar(`Attendance for "${selectedCourse.name}" updated successfully`, 'success');
                }
            } else {
                // Create new attendance
                // Attendance.courseName is always Courses.name (see src/utils/courseAttendance.js)
                await addDoc(attendanceRef, {
                    courseName: selectedCourse.name,
                    courseId: selectedCourse.id,
                    startTime: startTime,
                    endTime: endTime,
                    tableId: uuidv4(),
                    presentStudents,
                    absentStudents,
                    topic,
                    topicId: topicId || null,
                    comments,
                    leaveStudents,
                    leaveReasons,
                    campusCode: selectedCampus
                });

                showSnackbar(`Attendance for "${selectedCourse.name}" marked successfully`, 'success');
            }

            // Update original data after save
            setOriginalAttendanceData({
                courseId: selectedCourse.id,
                courseName: selectedCourse.name,
                presentStudents: [...presentStudents],
                absentStudents: [...absentStudents],
                leaveStudents: [...leaveStudents],
                leaveReasons: {...leaveReasons},
                topic: topic,
                topicId: topicId,
                comments: comments
            });
            setHasUnsavedChanges(false);

            setSelectedStudent(null);
            setSubjectWiseAttendance([]);
            setSessionData([]);
            
            // Refresh attendance data in context and update slot status
            await refreshCollection('attendance');
            await checkAllSlotsForDate();
        } catch (error) {
            console.error('Error marking attendance:', error);
            showSnackbar('Error marking attendance. Please try again.', 'error');
        } finally {
            setMarkingAttendance(false);
        }
    };


    const handleStudentSelect = async (studentId) => {
        const student = tableData.find(s => s.id === studentId);
        if (student) {
            setSelectedStudentDetail({
                ...student,
                attendanceSummary: null,
                subjectWiseAttendance: [],
                sessionData: []
            });
            setStudentDetailDialog(true);
            setAttendanceExpanded(false);
            
            // Calculate basic attendance summary
            const summary = await calculateAttendanceSummary(studentId);
            setSelectedStudentDetail(prev => ({
                ...prev,
                attendanceSummary: summary
            }));
            
            // Load subject-wise attendance by default
            await loadSubjectWiseAttendanceOnOpen(studentId);
        }
    };
    
    const handleViewAttendance = async (studentId) => {
        setSelectedStudent(studentId);
        const attendance = await calculateSubjectWiseAttendance(studentId);
        await fetchSessionData(studentId);
        setSubjectWiseAttendance(attendance);
    };
    
    const calculateAttendanceSummary = async (studentId) => {
        try {
            const attendanceRef = collection(db, 'Attendance');
            const qPresent = query(attendanceRef, where('presentStudents', 'array-contains', studentId));
            const qAbsent = query(attendanceRef, where('absentStudents', 'array-contains', studentId));
            const qLeave = query(attendanceRef, where('leaveStudents', 'array-contains', studentId));

            const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([
                getDocs(qPresent), 
                getDocs(qAbsent), 
                getDocs(qLeave)
            ]);

            const docMap = new Map();
            const mergeSnaps = (snap) => {
                snap.forEach((d) => {
                    if (!docMap.has(d.id)) docMap.set(d.id, d);
                });
            };
            mergeSnaps(presentSnapshot);
            mergeSnaps(absentSnapshot);
            mergeSnaps(leaveSnapshot);

            let totalPresent = 0;
            let totalAbsent = 0;
            let totalLeaves = 0;
            docMap.forEach((docSnap) => {
                const b = dedupeAttendanceBuckets(docSnap.data());
                if (b.presentSet.has(studentId)) totalPresent += 1;
                else if (b.leaveSet.has(studentId)) totalLeaves += 1;
                else if (b.absentSet.has(studentId)) totalAbsent += 1;
            });
            const total = totalPresent + totalAbsent + totalLeaves;
            const percentage = total > 0 ? (((totalPresent + totalLeaves) / total) * 100).toFixed(2) : 0;

            return {
                totalPresent,
                totalAbsent,
                totalLeaves,
                total,
                percentage
            };
        } catch (error) {
            console.error('Error calculating attendance summary:', error);
            return null;
        }
    };
    
    const handleExpandAttendance = async () => {
        if (!attendanceExpanded && selectedStudentDetail) {
            setLoadingAttendance(true);
            const attendance = await calculateSubjectWiseAttendance(selectedStudentDetail.id);
            const sessions = await fetchSessionDataForDialog(selectedStudentDetail.id);
            setSelectedStudentDetail(prev => ({
                ...prev,
                subjectWiseAttendance: attendance,
                sessionData: sessions
            }));
            setLoadingAttendance(false);
        }
        setAttendanceExpanded(!attendanceExpanded);
    };
    
    const loadSubjectWiseAttendanceOnOpen = async (studentId) => {
        setLoadingAttendance(true);
        const attendance = await calculateSubjectWiseAttendance(studentId);
        const sessions = await fetchSessionDataForDialog(studentId);
        setSelectedStudentDetail(prev => ({
            ...prev,
            subjectWiseAttendance: attendance,
            sessionData: sessions
        }));
        setLoadingAttendance(false);
        setAttendanceExpanded(true);
    };

    const calculateSubjectWiseAttendance = async (studentId) => {
        try {
            const attendanceRef = collection(db, 'Attendance');
            
            // Get the student's campusCode first
            const studentsRef = collection(db, 'Students');
            const studentQuery = query(studentsRef, where('id', '==', studentId));
            const studentSnapshot = await getDocs(studentQuery);
            let studentCampusCode = null;
            studentSnapshot.forEach(doc => {
                studentCampusCode = doc.data().campusCode;
            });
            
            // Try to get courses from cache first
            let courseTotalClasses = {};
            const cachedCourses = localStorage.getItem('courseTotalClasses');
            const cacheTime = localStorage.getItem('courseTotalClasses_timestamp');
            const now = Date.now();
            
            // Use cache if it's less than 1 hour old
            if (cachedCourses && cacheTime && (now - parseInt(cacheTime) < 3600000)) {
                courseTotalClasses = JSON.parse(cachedCourses);
            } else {
                // Fetch courses and cache them
                const coursesRef = collection(db, 'Courses');
                const coursesSnapshot = await getDocs(coursesRef);
                coursesSnapshot.forEach(doc => {
                    const data = doc.data();
                    courseTotalClasses[data.name] = data.totalClasses || 0;
                });
                localStorage.setItem('courseTotalClasses', JSON.stringify(courseTotalClasses));
                localStorage.setItem('courseTotalClasses_timestamp', now.toString());
            }
            
            // Fetch attendance records FILTERED BY CAMPUS to count conducted classes
            let allAttendanceSnapshot;
            if (studentCampusCode) {
                const campusQuery = query(attendanceRef, where('campusCode', '==', studentCampusCode));
                allAttendanceSnapshot = await getDocs(campusQuery);
            } else {
                // Fallback to all attendance if no campus found
                allAttendanceSnapshot = await getDocs(attendanceRef);
            }
            
            // Count conducted classes by course (filtered by campus)
            const conductedClassesBySubject = {};
            allAttendanceSnapshot.forEach(doc => {
                const courseName = doc.data().courseName;
                conductedClassesBySubject[courseName] = (conductedClassesBySubject[courseName] || 0) + 1;
            });
            
            const qPresent = query(attendanceRef, where('presentStudents', 'array-contains', studentId));
            const qAbsent = query(attendanceRef, where('absentStudents', 'array-contains', studentId));
            const qLeave = query(attendanceRef, where('leaveStudents', 'array-contains', studentId));

            const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([
                getDocs(qPresent), 
                getDocs(qAbsent), 
                getDocs(qLeave)
            ]);

            const attendanceBySubject = {};

            const ensureSubjectRow = (courseName) => {
                if (!courseName) return null;
                if (!attendanceBySubject[courseName]) {
                    attendanceBySubject[courseName] = {
                        present: 0,
                        absent: 0,
                        leaves: 0,
                        conducted: conductedClassesBySubject[courseName] || 0,
                        totalPlanned: courseTotalClasses[courseName] || 0,
                        percentage: 0
                    };
                }
                return attendanceBySubject[courseName];
            };

            const docMapSubject = new Map();
            const mergeSubjectSnaps = (snap) => {
                snap.forEach((d) => {
                    if (!docMapSubject.has(d.id)) docMapSubject.set(d.id, d);
                });
            };
            mergeSubjectSnaps(presentSnapshot);
            mergeSubjectSnaps(absentSnapshot);
            mergeSubjectSnaps(leaveSnapshot);

            docMapSubject.forEach((docSnap) => {
                const data = docSnap.data();
                const courseName = data.courseName;
                const row = ensureSubjectRow(courseName);
                if (!row) return;
                const b = dedupeAttendanceBuckets(data);
                if (b.presentSet.has(studentId)) row.present += 1;
                else if (b.leaveSet.has(studentId)) row.leaves += 1;
                else if (b.absentSet.has(studentId)) row.absent += 1;
            });

            // Calculate the percentage for each subject based on conducted classes
            Object.keys(attendanceBySubject).forEach((courseName) => {
                const subjectData = attendanceBySubject[courseName];
                if (subjectData.conducted > 0) {
                    const attended = subjectData.present + subjectData.leaves;
                    subjectData.percentage = ((attended / subjectData.conducted) * 100).toFixed(2);
                } else {
                    subjectData.percentage = '0.00';
                }
            });

            return Object.entries(attendanceBySubject)
                .map(([subject, counts]) => ({
                    subject,
                    ...counts,
                }))
                .sort((a, b) => a.subject.localeCompare(b.subject));
        } catch (error) {
            console.error('Error fetching attendance records: ', error);
            return [];
        }
    };

    const sessionColumns = [
        {field: 'courseName', headerName: 'Course Name', width: 200},
        {field: 'startTime', headerName: 'Start Time', width: 200},
        {field: 'endTime', headerName: 'End Time', width: 200},
        {field: 'status', headerName: 'Status', width: 100},
        {field: 'reason', headerName: 'Leave Reason', width: 200}  // Adding reason to session data
    ];

    const columns = [
        {
            field: 'id',
            headerName: 'Student ID',
            width: 160,
            renderCell: (params) => (
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => handleStudentSelect(params.row.id)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {params.value}
                </Button>
            )
        },
        {field: 'name', headerName: 'Name', width: 150},
        {field: 'gender', headerName: 'Gender', width: 100},
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            type: 'singleSelect',
            valueOptions: Object.values(STUDENT_STATUS),
            renderCell: (params) => (
                <Chip
                    label={getStudentStatusLabel(params.value)}
                    color={isActiveLikeStatus(params.value) ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'present',
            headerName: 'Present',
            disableExport: true,
            renderCell: (params) => (
                <Checkbox
                    checked={presentStudents.includes(params.row.id)}
                    onChange={() => handlePresentToggle(params.row.id)}
                />
            ),
        },
        {
            field: 'absent',
            headerName: 'Absent',
            disableExport: true,
            renderCell: (params) => (
                <Checkbox
                    checked={absentStudents.includes(params.row.id)}
                    onChange={() => handleAbsentToggle(params.row.id)}
                />
            ),
        },
        {
            field: 'leave',
            headerName: 'Leave',
            disableExport: true,
            width: 400, // Increase width to prevent cramping
            renderCell: (params) => (
                <>
                    <Checkbox
                        checked={leaveStudents.includes(params.row.id)}
                        onChange={() => handleLeaveToggle(params.row.id)}
                    />
                    {leaveStudents.includes(params.row.id) && (
                        <TextField
                            value={leaveReasons[params.row.id] || ''}
                            onChange={(e) => handleLeaveReasonChange(params.row.id, e.target.value)}
                            variant="outlined"
                            onKeyDown={(e) => {
                                if (e.key === ' ') {
                                    e.stopPropagation();  // Stop the space bar from triggering DataGrid events
                                }
                            }}
                            placeholder="Reason for leave"
                        />
                    )}
                </>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            disableExport: true,
            renderCell: (params) => (
                <IconButton 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => handleViewAttendance(params.row.id)}
                    size="small"
                >
                    <InfoIcon/>
                </IconButton>
            ),
        }
    ];

    const attendanceColumns = [
        {field: 'subject', headerName: 'Subject', width: 200},
        {field: 'present', headerName: 'Present', width: 100},
        {field: 'absent', headerName: 'Absent', width: 100},
        {field: 'leaves', headerName: 'Leaves', width: 100},
        {field: 'total', headerName: 'Total', width: 100},
        {field: 'percentage', headerName: 'Attendance %', width: 110},
    ];

    const fetchSessionData = async (studentId) => {
        try {
            const attendanceRef = collection(db, 'Attendance');
            const qPresent = query(attendanceRef, where('presentStudents', 'array-contains', studentId));
            const qAbsent = query(attendanceRef, where('absentStudents', 'array-contains', studentId));
            const qLeave = query(attendanceRef, where('leaveStudents', 'array-contains', studentId));

            const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([getDocs(qPresent), getDocs(qAbsent), getDocs(qLeave)]);

            const sessions = [];
            const docMapSession = new Map();
            const mergeSessionSnaps = (snap) => {
                snap.forEach((d) => {
                    if (!docMapSession.has(d.id)) docMapSession.set(d.id, d);
                });
            };
            mergeSessionSnaps(presentSnapshot);
            mergeSessionSnaps(absentSnapshot);
            mergeSessionSnaps(leaveSnapshot);

            docMapSession.forEach((doc) => {
                const data = doc.data();
                const b = dedupeAttendanceBuckets(data);
                let status;
                let reason;
                if (b.presentSet.has(studentId)) {
                    status = 'Present';
                    reason = undefined;
                } else if (b.leaveSet.has(studentId)) {
                    status = 'Leave';
                    reason = (data.leaveReasons && data.leaveReasons[studentId]) || leaveReasons[doc.id] || 'N/A';
                } else if (b.absentSet.has(studentId)) {
                    status = 'Absent';
                    reason = undefined;
                } else {
                    return;
                }
                const start = dayjs(data.startTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
                const end = dayjs(data.endTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
                sessions.push({
                    courseName: data.courseName,
                    startTime: start.format('DD/MM/YYYY - hh:mm A'),
                    endTime: end.format('hh:mm A'),
                    tableId: data.tableId,
                    status,
                    reason
                });
            });

            setSessionData(sessions);
        } catch (error) {
            console.error('Error fetching session data: ', error);
        }
    };

    const fetchSessionDataForDialog = async (studentId) => {
        try {
            const attendanceRef = collection(db, 'Attendance');
            const qPresent = query(attendanceRef, where('presentStudents', 'array-contains', studentId));
            const qAbsent = query(attendanceRef, where('absentStudents', 'array-contains', studentId));
            const qLeave = query(attendanceRef, where('leaveStudents', 'array-contains', studentId));

            const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([getDocs(qPresent), getDocs(qAbsent), getDocs(qLeave)]);

            const sessions = [];
            const docMapDialog = new Map();
            const mergeDialogSnaps = (snap) => {
                snap.forEach((d) => {
                    if (!docMapDialog.has(d.id)) docMapDialog.set(d.id, d);
                });
            };
            mergeDialogSnaps(presentSnapshot);
            mergeDialogSnaps(absentSnapshot);
            mergeDialogSnaps(leaveSnapshot);

            docMapDialog.forEach((doc) => {
                const data = doc.data();
                const b = dedupeAttendanceBuckets(data);
                let status;
                let reason = null;
                if (b.presentSet.has(studentId)) {
                    status = 'Present';
                } else if (b.leaveSet.has(studentId)) {
                    status = 'Leave';
                    reason = data.leaveReasons && data.leaveReasons[studentId] ? data.leaveReasons[studentId] : null;
                } else if (b.absentSet.has(studentId)) {
                    status = 'Absent';
                } else {
                    return;
                }
                sessions.push({
                    courseName: data.courseName,
                    startTime: data.startTime,
                    endTime: data.endTime,
                    status,
                    reason
                });
            });

            // Sort by date descending (most recent first)
            sessions.sort((a, b) => {
                const timeA = a.startTime?.toDate ? a.startTime.toDate() : new Date(a.startTime);
                const timeB = b.startTime?.toDate ? b.startTime.toDate() : new Date(b.startTime);
                return timeB - timeA;
            });

            return sessions;
        } catch (error) {
            console.error('Error fetching session data for dialog: ', error);
            return [];
        }
    };
    
    const handleNavigateStudent = async (direction) => {
        if (!selectedStudentDetail) return;
        
        const currentIndex = tableData.findIndex(s => s.id === selectedStudentDetail.id);
        let newIndex;
        
        if (direction === 'next') {
            newIndex = currentIndex + 1;
            if (newIndex >= tableData.length) newIndex = 0; // Loop to first
        } else {
            newIndex = currentIndex - 1;
            if (newIndex < 0) newIndex = tableData.length - 1; // Loop to last
        }
        
        const newStudent = tableData[newIndex];
        if (newStudent) {
            await handleStudentSelect(newStudent.id);
        }
    };
    
    const handlePrintStudentReport = () => {
        if (!selectedStudentDetail) return;

        const printWindow = window.open('', '_blank');
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Student Report - ${selectedStudentDetail.name}</title>
            <style>
              @media print {
                body { margin: 0; }
                @page { margin: 1cm; }
              }
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                direction: ltr;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 3px solid #4CAF50;
                padding-bottom: 15px;
              }
              .header h1 {
                color: #4CAF50;
                margin: 10px 0;
              }
              .section {
                margin: 30px 0;
                page-break-inside: avoid;
              }
              .section-title {
                background-color: #4CAF50;
                color: white;
                padding: 10px;
                font-size: 18px;
                font-weight: bold;
                margin-bottom: 15px;
              }
              .profile-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 15px;
                margin-bottom: 20px;
              }
              .profile-item {
                padding: 10px;
                border: 1px solid #ddd;
                border-radius: 5px;
              }
              .profile-label {
                font-weight: bold;
                color: #555;
                font-size: 12px;
                margin-bottom: 5px;
              }
              .profile-value {
                font-size: 14px;
                color: #000;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 10px;
                text-align: left;
              }
              th {
                background-color: #4CAF50;
                color: white;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .status-chip {
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: bold;
              }
              .status-present { background-color: #4CAF50; color: white; }
              .status-absent { background-color: #f44336; color: white; }
              .status-leave { background-color: #ff9800; color: white; }
              .footer {
                margin-top: 40px;
                text-align: center;
                font-size: 12px;
                color: #666;
                border-top: 2px solid #4CAF50;
                padding-top: 15px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Student Report</h1>
              <p>Anjuman Khuddam ul Quran</p>
              <p style="font-size: 14px; color: #666;">Generated on ${new Date().toLocaleString('en-GB')}</p>
            </div>

            <!-- Profile Section -->
            <div class="section">
              <div class="section-title">📋 Student Profile</div>
              <div class="profile-grid">
                <div class="profile-item">
                  <div class="profile-label">Name</div>
                  <div class="profile-value">${selectedStudentDetail.name}</div>
                </div>
                <div class="profile-item">
                  <div class="profile-label">Student ID</div>
                  <div class="profile-value">${selectedStudentDetail.id}</div>
                </div>
                <div class="profile-item">
                  <div class="profile-label">Status</div>
                  <div class="profile-value">${getStudentStatusLabel(selectedStudentDetail.status)}</div>
                </div>
                <div class="profile-item">
                  <div class="profile-label">Campus Name</div>
                  <div class="profile-value">${selectedStudentDetail.campusName || selectedStudentDetail.campusCode || '-'}</div>
                </div>
              </div>
            </div>

            <!-- Attendance Summary Section -->
            ${selectedStudentDetail.subjectWiseAttendance?.length > 0 ? `
            <div class="section">
              <div class="section-title">📊 Attendance Summary</div>
              <table>
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Leaves</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${selectedStudentDetail.subjectWiseAttendance.map(row => `
                    <tr>
                      <td>${row.subject}</td>
                      <td>${row.present}</td>
                      <td>${row.absent}</td>
                      <td>${row.leaves}</td>
                      <td>${row.percentage}%</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            ` : ''}

            <!-- Recent Sessions Section -->
            ${sessionData?.length > 0 ? `
            <div class="section">
              <div class="section-title">📅 Recent Sessions (Last 5)</div>
              <table>
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  ${sessionData.slice(0, 5).map(session => `
                    <tr>
                      <td>${session.courseName}</td>
                      <td>${session.startTime}</td>
                      <td><span class="status-chip status-${session.status.toLowerCase()}">${session.status}</span></td>
                      <td>${session.reason || '-'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            ` : ''}

            <div class="footer">
              <p>© Anjuman Khuddam ul Quran</p>
              <p>This is a computer-generated report</p>
            </div>
          </body>
          </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();
        
        setTimeout(() => {
          printWindow.print();
        }, 250);
    };

    const handlePrintAttendance = () => {
        // Get selected rows or all rows if none selected
        const rowsToPrint = selectedAttendanceRows.length > 0 
            ? allAttendanceData.filter(row => selectedAttendanceRows.includes(row.id))
            : allAttendanceData;

        if (rowsToPrint.length === 0) {
            alert('No records to print');
            return;
        }

        // Create print content
        const printWindow = window.open('', '_blank');
        const studentName = selectedStudentDetail?.name || 'Student';
        const studentId = selectedStudentDetail?.id || '';
        
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title>Attendance Report - ${studentName}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap" rel="stylesheet">
                <style>
                    @media print {
                        body { margin: 0; }
                        @page { margin: 1cm; }
                    }
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                    }
                    .urdu {
                        font-family: 'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif;
                        font-weight: 400;
                        -webkit-font-smoothing: antialiased;
                        -moz-osx-font-smoothing: grayscale;
                        direction: ltr;
                        text-align: left;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                        border-bottom: 3px solid #4CAF50;
                        padding-bottom: 15px;
                    }
                    .header h1 {
                        color: #4CAF50;
                        margin: 10px 0;
                    }
                    .info {
                        margin-bottom: 20px;
                        display: flex;
                        justify-content: space-between;
                    }
                    .info div {
                        font-size: 14px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 20px;
                    }
                    th, td {
                        border: 1px solid #ddd;
                        padding: 10px;
                        text-align: left;
                    }
                    th {
                        background-color: #4CAF50;
                        color: white;
                        font-weight: bold;
                    }
                    tr:nth-child(even) {
                        background-color: #f9f9f9;
                    }
                    .status-present {
                        color: #4CAF50;
                        font-weight: bold;
                    }
                    .status-absent {
                        color: #f44336;
                        font-weight: bold;
                    }
                    .status-leave {
                        color: #ff9800;
                        font-weight: bold;
                    }
                    .footer {
                        margin-top: 40px;
                        text-align: center;
                        font-size: 12px;
                        color: #666;
                    }
                    .summary {
                        margin: 20px 0;
                        padding: 15px;
                        background-color: #f5f5f5;
                        border-radius: 5px;
                    }
                    .summary-item {
                        display: inline-block;
                        margin-right: 30px;
                        font-weight: bold;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Attendance Report</h1>
                    <p>Anjuman Khuddam ul Quran</p>
                </div>
                
                <div class="info">
                    <div><strong>Student Name:</strong> ${studentName}</div>
                    <div><strong>Student ID:</strong> ${studentId}</div>
                    <div><strong>Report Date:</strong> ${new Date().toLocaleDateString('en-GB')}</div>
                </div>

                <div class="summary">
                    <span class="summary-item">Total Records: ${rowsToPrint.length}</span>
                    <span class="summary-item" style="color: #4CAF50;">Present: ${rowsToPrint.filter(r => r.status === 'Present').length}</span>
                    <span class="summary-item" style="color: #f44336;">Absent: ${rowsToPrint.filter(r => r.status === 'Absent').length}</span>
                    <span class="summary-item" style="color: #ff9800;">Leave: ${rowsToPrint.filter(r => r.status === 'Leave').length}</span>
                </div>
                
                <table>
                    <thead>
                        <tr>
                            <th style="width: 5%;">#</th>
                            <th style="width: 20%;">Course</th>
                            <th style="width: 15%;">Date & Time</th>
                            <th style="width: 25%;">Topic</th>
                            <th style="width: 10%;">Status</th>
                            <th style="width: 15%;">Leave Reason</th>
                            <th style="width: 10%;">Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsToPrint.map((row, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${row.courseName}</td>
                                <td>${row.startTime}</td>
                                <td class="${isUrduText(String(row.topic || '')) ? 'urdu' : ''}">${row.topic}</td>
                                <td class="status-${row.status.toLowerCase()}">${row.status}</td>
                                <td>${row.reason || '-'}</td>
                                <td class="${isUrduText(String(row.comments || '')) ? 'urdu' : ''}">${row.comments}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                <div class="footer">
                    <p>Generated on ${new Date().toLocaleString('en-GB')}</p>
                    <p>© Anjuman Khuddam ul Quran</p>
                </div>
            </body>
            </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for content to load then print
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    // Download Attendance Summary as CSV
    const handleDownloadAttendanceSummary = () => {
        if (!selectedStudentDetail || !selectedStudentDetail.subjectWiseAttendance || selectedStudentDetail.subjectWiseAttendance.length === 0) {
            return;
        }

        const headers = ['Subject', 'Present', 'Absent', 'Leaves', 'Conducted', 'Total Planned', 'Percentage'];
        const rows = selectedStudentDetail.subjectWiseAttendance.map(row => [
            row.subject,
            row.present,
            row.absent,
            row.leaves,
            row.conducted,
            row.totalPlanned,
            `${row.percentage}%`
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell)}"`).join(','))
        ].join('\n');

        // Add UTF-8 BOM for proper Urdu/Arabic text display in Excel
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const studentName = selectedStudentDetail.name || selectedStudentDetail.id;
        link.setAttribute('download', `${studentName}_Attendance_Summary_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleViewAllAttendance = async () => {
        if (!selectedStudentDetail) return;
        
        setLoadingAllAttendance(true);
        setAllAttendanceDialog(true);
        
        try {
            const attendanceRef = collection(db, 'Attendance');
            const qPresent = query(attendanceRef, where('presentStudents', 'array-contains', selectedStudentDetail.id));
            const qAbsent = query(attendanceRef, where('absentStudents', 'array-contains', selectedStudentDetail.id));
            const qLeave = query(attendanceRef, where('leaveStudents', 'array-contains', selectedStudentDetail.id));

            const [presentSnapshot, absentSnapshot, leaveSnapshot] = await Promise.all([
                getDocs(qPresent), 
                getDocs(qAbsent), 
                getDocs(qLeave)
            ]);

            const allSessions = [];
            let idCounter = 1;
            const sid = selectedStudentDetail.id;
            const docMapAll = new Map();
            const mergeAllSnaps = (snap) => {
                snap.forEach((d) => {
                    if (!docMapAll.has(d.id)) docMapAll.set(d.id, d);
                });
            };
            mergeAllSnaps(presentSnapshot);
            mergeAllSnaps(absentSnapshot);
            mergeAllSnaps(leaveSnapshot);

            docMapAll.forEach((doc) => {
                const data = doc.data();
                const b = dedupeAttendanceBuckets(data);
                let status;
                let reason = null;
                if (b.presentSet.has(sid)) {
                    status = 'Present';
                } else if (b.leaveSet.has(sid)) {
                    status = 'Leave';
                    reason = data.leaveReasons && data.leaveReasons[sid] ? data.leaveReasons[sid] : null;
                } else if (b.absentSet.has(sid)) {
                    status = 'Absent';
                } else {
                    return;
                }
                const start = dayjs(data.startTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
                const end = dayjs(data.endTime.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');

                allSessions.push({
                    id: idCounter++,
                    courseName: data.courseName,
                    startTime: start.isValid() ? start.format('DD/MM/YYYY - hh:mm A') : data.startTime,
                    endTime: end.isValid() ? end.format('hh:mm A') : data.endTime,
                    startValue: start.isValid() ? start.valueOf() : 0,
                    topic: data.topic || '-',
                    comments: data.comments || '-',
                    status,
                    reason
                });
            });

            // Sort by date descending (most recent first)
            allSessions.sort((a, b) => b.startValue - a.startValue);

            setAllAttendanceData(allSessions);
        } catch (error) {
            console.error('Error fetching all attendance: ', error);
            setAllAttendanceData([]);
        } finally {
            setLoadingAllAttendance(false);
        }
    };

    const handleMarkAllPresent = async () => {
        const filteredStudentIds = getFilteredStudentIds();
        if (filteredStudentIds.length === 0) {
            showSnackbar('No students match the current filters.', 'warning');
            return;
        }

        const filteredIdSet = new Set(filteredStudentIds);
        setPresentStudents([
            ...presentStudents.filter(id => !filteredIdSet.has(id)),
            ...filteredStudentIds,
        ]);
        setAbsentStudents(absentStudents.filter(id => !filteredIdSet.has(id)));
        setLeaveStudents(leaveStudents.filter(id => !filteredIdSet.has(id)));
        setLeaveReasons(
            Object.fromEntries(
                Object.entries(leaveReasons).filter(([sid]) => !filteredIdSet.has(sid))
            )
        );
        showSnackbar(`${filteredStudentIds.length} filtered student${filteredStudentIds.length === 1 ? '' : 's'} marked as present`, 'info');
    };

    const handleMarkAllAbsent = async () => {
        const filteredStudentIds = getFilteredStudentIds();
        if (filteredStudentIds.length === 0) {
            showSnackbar('No students match the current filters.', 'warning');
            return;
        }

        const filteredIdSet = new Set(filteredStudentIds);
        setAbsentStudents([
            ...absentStudents.filter(id => !filteredIdSet.has(id)),
            ...filteredStudentIds,
        ]);
        setPresentStudents(presentStudents.filter(id => !filteredIdSet.has(id)));
        setLeaveStudents(leaveStudents.filter(id => !filteredIdSet.has(id)));
        setLeaveReasons(
            Object.fromEntries(
                Object.entries(leaveReasons).filter(([sid]) => !filteredIdSet.has(sid))
            )
        );
        showSnackbar(`${filteredStudentIds.length} filtered student${filteredStudentIds.length === 1 ? '' : 's'} marked as absent`, 'info');
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };


    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 700, 
                            color: '#2E7D32',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        📊 Attendance Management
                    </Typography>
                </Box>

                {/* Campus Selection Card */}
                <Paper 
                    elevation={2} 
                    sx={{ 
                        p: 2, 
                        mb: 3, 
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                        border: '1px solid rgba(76, 175, 80, 0.1)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                fontWeight: 600, 
                                color: '#2E7D32',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 'fit-content'
                            }}
                        >
                            🏫 Campus Selection
                        </Typography>
                        <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
                            <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
                            <Select
                                value={selectedCampus}
                                onChange={handleCampusChange}
                                input={<OutlinedInput label="Select Campus" />}
                                disabled={campuses.length === 1}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover fieldset': {
                                            borderColor: '#4CAF50',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2E7D32',
                                        },
                                    },
                                }}
                            >
                                {campuses.map((campus) => (
                                    <MenuItem key={campus.id} value={campus.campusCode}>
                                        {campus.campusName} ({campus.campusCode})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Paper>

                {/* Time and Course Selection Card */}
                {selectedCampus && (
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                            border: '1px solid rgba(76, 175, 80, 0.1)'
                        }}
                    >
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600, 
                            color: '#2E7D32',
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        ⏰ Session Details
                    </Typography>

                    {/* Date Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#4CAF50' }}>
                            📅 Select Date
                        </Typography>
                        <DatePicker
                            value={selectedDate}
                            disableFuture={true}
                            onChange={handleDateChange}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Select Date"
                                    sx={{
                                        minWidth: 250,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '&:hover fieldset': {
                                                borderColor: '#4CAF50',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#2E7D32',
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                        {selectedDate && (
                            <Chip 
                                label={selectedDate.format('dddd, MMMM D, YYYY')}
                                color="primary"
                                sx={{ mt: 2, fontWeight: 600 }}
                            />
                        )}
                    </Box>

                    {/* Slot Selection */}
                    {selectedDate && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#4CAF50' }}>
                                ⏰ Select Time Slot
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {getAvailableSlots().map((slot) => {
                                    const isSelected = selectedSlot?.id === slot.id;
                                    const hasAttendance = slotAttendanceStatus[slot.id] || false;
                                    const slotCourseName = slotCourseMap[slot.id];
                                    
                                    return (
                                        <Paper
                                            key={slot.id}
                                            elevation={isSelected ? 4 : 1}
                                            onClick={() => handleSlotSelect(slot)}
                                            sx={{
                                                p: 2.5,
                                                cursor: 'pointer',
                                                borderRadius: 3,
                                                border: isSelected ? '2px solid #4CAF50' : '2px solid transparent',
                                                backgroundColor: isSelected 
                                                    ? 'rgba(76, 175, 80, 0.08)' 
                                                    : hasAttendance 
                                                        ? 'rgba(76, 175, 80, 0.05)' 
                                                        : '#fff',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                                                    borderColor: '#4CAF50',
                                                },
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: '50%',
                                                            backgroundColor: isSelected 
                                                                ? '#4CAF50' 
                                                                : hasAttendance 
                                                                    ? '#4CAF50' 
                                                                    : '#e0e0e0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontSize: '1.1rem',
                                                            boxShadow: isSelected 
                                                                ? '0 4px 12px rgba(76, 175, 80, 0.4)' 
                                                                : '0 2px 8px rgba(0,0,0,0.1)'
                                                        }}
                                                    >
                                                        {slot.id.replace('slot', '')}
                                                    </Box>
                                                    <Box>
                                                        <Typography 
                                                            variant="h6" 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                color: isSelected ? '#2E7D32' : '#333',
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            {slot.label}
                                                        </Typography>
                                                        {hasAttendance && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', mt: 0.5 }}>
                                                                <Chip 
                                                                    label="Attendance Marked" 
                                                                    size="small" 
                                                                    color="success"
                                                                    sx={{ 
                                                                        fontSize: '0.7rem',
                                                                        height: 20,
                                                                        fontWeight: 600
                                                                    }}
                                                                />
                                                                {slotCourseName && (
                                                                    <Chip 
                                                                        label={slotCourseName}
                                                                        size="small" 
                                                                        color="info"
                                                                        variant="outlined"
                                                                        sx={{ 
                                                                            fontSize: '0.65rem',
                                                                            height: 18,
                                                                            fontWeight: 500,
                                                                            maxWidth: '200px',
                                                                            '& .MuiChip-label': {
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {isSelected && (
                                                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 32 }} />
                                                    )}
                                                    {/* Admin controls for all slots - Delete only */}
                                                    {isAdmin() && hasAttendance && slotAttendanceMap[slot.id] && (
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            onClick={async (e) => {
                                                                e.stopPropagation(); // Prevent slot selection
                                                                const attendance = slotAttendanceMap[slot.id];
                                                                if (attendance && window.confirm(`Are you sure you want to delete attendance for "${attendance.courseName}" - ${slot.label}?`)) {
                                                                    try {
                                                                        setDeletingAttendance(true);
                                                                        await deleteDoc(doc(db, 'Attendance', attendance.id));
                                                                        showSnackbar(`Attendance deleted successfully`, 'success');
                                                                        await refreshCollection('attendance');
                                                                        await checkAllSlotsForDate();
                                                                    } catch (error) {
                                                                        console.error('Error deleting attendance:', error);
                                                                        showSnackbar('Error deleting attendance. Please try again.', 'error');
                                                                    } finally {
                                                                        setDeletingAttendance(false);
                                                                    }
                                                                }
                                                            }}
                                                            disabled={deletingAttendance}
                                                            sx={{
                                                                borderRadius: 2,
                                                                textTransform: 'none',
                                                                fontWeight: 500,
                                                                minWidth: 'auto',
                                                                px: 1.5,
                                                                ml: 1
                                                            }}
                                                        >
                                                            🗑️
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>
                            {getAvailableSlots().length === 0 && (
                                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                    No slots available for {selectedDate.format('dddd')}. Please select Monday to Friday.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Extra Attendance Records Notification and List */}
                    {selectedDate && extraAttendanceRecords.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                                ⚠️ Found {extraAttendanceRecords.length} extra attendance record(s) for this date. 
                                Expected: {getAvailableSlots().length} slot(s) ({selectedDate.format('dddd')}), 
                                Found: {getAvailableSlots().length + extraAttendanceRecords.length} record(s).
                                {isAdmin() && ' Use admin controls on slots above to manage them.'}
                            </Alert>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#ff9800' }}>
                                📋 Extra Attendance Records
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {extraAttendanceRecords.map((record, index) => {
                                    // Parse time from startTime
                                    const timeMatch = record.startTime?.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                                    const timeStr = timeMatch ? `${timeMatch[2]}:${timeMatch[3]}` : 'N/A';
                                    const endTimeMatch = record.endTime?.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                                    const endTimeStr = endTimeMatch ? `${endTimeMatch[2]}:${endTimeMatch[3]}` : 'N/A';
                                    
                                    return (
                                        <Paper
                                            key={record.id || index}
                                            elevation={1}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid #ff9800',
                                                backgroundColor: '#fff3e0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Chip 
                                                        label={record.courseName || 'Unknown Course'}
                                                        size="small"
                                                        color="warning"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {timeStr} - {endTimeStr}
                                                    </Typography>
                                                </Box>
                                                {record.topic && (
                                                    <Typography
                                                        variant="body2"
                                                        className={isUrduText(record.topic || '') ? 'urdu' : ''}
                                                        sx={{
                                                            color: '#666',
                                                            fontSize: '0.85rem',
                                                            ...getUrduSxProps(record.topic || ''),
                                                            direction: 'ltr',
                                                            textAlign: 'left'
                                                        }}
                                                    >
                                                        Topic: {record.topic}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {/* Admin controls for extra records - Delete only */}
                                            {isAdmin() && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={async () => {
                                                        if (record && window.confirm(`Are you sure you want to delete attendance for "${record.courseName}"?`)) {
                                                            try {
                                                                setDeletingAttendance(true);
                                                                await deleteDoc(doc(db, 'Attendance', record.id));
                                                                showSnackbar(`Attendance deleted successfully`, 'success');
                                                                await refreshCollection('attendance');
                                                                await checkAllSlotsForDate();
                                                            } catch (error) {
                                                                console.error('Error deleting attendance:', error);
                                                                showSnackbar('Error deleting attendance. Please try again.', 'error');
                                                            } finally {
                                                                setDeletingAttendance(false);
                                                            }
                                                        }
                                                    }}
                                                    disabled={deletingAttendance}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    🗑️ Delete
                                                </Button>
                                            )}
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Subject Selection */}
                    {selectedSlot && getAvailableSlots().length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#4CAF50' }}>
                            Select Subject
                        </Typography>
                        {hasUnsavedChanges && selectedSlot && selectedCourse?.name && (presentStudents.length || absentStudents.length || leaveStudents.length) ? (
                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                💡 You have unsaved changes. Click "Mark Attendance" to save.
                            </Alert>
                        ) : null}
                        <Box display="flex" gap={1} flexWrap="wrap">
                            {courses.map((course) => (
                                <Button
                                    key={course.id}
                                    variant={
                                        (
                                            String(selectedCourse?.id) === String(course?.id) ||
                                            (selectedCourse?.name && course?.name &&
                                                selectedCourse.name.trim().toLowerCase() === course.name.trim().toLowerCase())
                                        )
                                            ? "contained"
                                            : "outlined"
                                    }
                                    color={
                                        (
                                            String(selectedCourse?.id) === String(course?.id) ||
                                            (selectedCourse?.name && course?.name &&
                                                selectedCourse.name.trim().toLowerCase() === course.name.trim().toLowerCase())
                                        )
                                            ? 'success'
                                            : "primary"
                                    }
                                    onClick={() => handleCourseSelect(course)}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 3,
                                        py: 1,
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {course.name}
                                </Button>
                            ))}
                        </Box> 
                        {selectedCourse && (
                            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                <strong>Selected Subject:</strong> {selectedCourse.name}
                            </Alert>
                        )}
                    </Box>
                    )}

                    {/* Topic and Comments */}
                    {selectedSlot && getAvailableSlots().length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedCourse && hasPredefinedTopics(selectedCourse.name) ? (
                            <Autocomplete
                                freeSolo
                                options={availableTopics}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    if (option && typeof option === 'object') return option.name || '';
                                    return '';
                                }}
                                value={topic ? (availableTopics.find(t => t.name === topic) || topic) : null}
                                onChange={(event, newValue) => {
                                    if (newValue && typeof newValue === 'object' && newValue.name) {
                                        setTopic(newValue.name);
                                        setTopicId(newValue.topicId || newValue.id || '');
                                    } else if (typeof newValue === 'string') {
                                        setTopic(newValue);
                                        setTopicId(''); // Custom topic, no ID
                                    } else {
                                        setTopic('');
                                        setTopicId('');
                                    }
                                }}
                                onInputChange={(event, newInputValue, reason) => {
                                    // Only update on user input, not when selecting from dropdown
                                    if (reason === 'input') {
                                        setTopic(newInputValue);
                                        // If user types custom text, clear topicId
                                        const matchingTopic = availableTopics.find(t => t.name === newInputValue);
                                        if (matchingTopic) {
                                            setTopicId(matchingTopic.topicId || matchingTopic.id || '');
                                        } else {
                                            setTopicId('');
                                        }
                                    }
                                }}
                                renderOption={(props, option) => {
                                    const optionName = typeof option === 'string' ? option : (option.name || '');
                                    const urdu = isUrduText(optionName);
                                    return (
                                        <li {...props} style={{ direction: 'ltr', textAlign: 'left' }}>
                                            <Typography
                                                className={urdu ? 'urdu' : ''}
                                                sx={{
                                                    ...getUrduSxProps(optionName),
                                                    direction: 'ltr',
                                                    textAlign: 'left',
                                                    width: '100%'
                                                }}
                                            >
                                                {optionName}
                                            </Typography>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Topic Covered"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '&:hover fieldset': {
                                                    borderColor: '#4CAF50',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2E7D32',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontFamily: isUrduText(topic)
                                                    ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                                                    : 'inherit',
                                                direction: 'ltr',
                                                textAlign: 'left'
                                            }
                                        }}
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                label="Topic Covered"
                                value={topic}
                                onChange={(e) => {
                                    setTopic(e.target.value);
                                    setTopicId(''); // Manual entry, no topicId
                                }}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '&:hover fieldset': {
                                            borderColor: '#4CAF50',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2E7D32',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: isUrduText(topic)
                                            ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                                            : 'inherit',
                                        direction: 'ltr',
                                        textAlign: 'left'
                                    }
                                }}
                            />
                        )}
                        <TextField
                            label="Comments/Notes"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover fieldset': {
                                        borderColor: '#4CAF50',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#2E7D32',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: isUrduText(comments)
                                        ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif"
                                        : 'inherit',
                                    direction: 'ltr',
                                    textAlign: 'left'
                                }
                            }}
                        />
                    </Box>
                    )}
                </Paper>
                )}

                                 {/* Action Buttons Card */}
                 {selectedCampus && selectedDate && getAvailableSlots().length > 0 && (
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                            border: '1px solid rgba(76, 175, 80, 0.1)'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 600, 
                                color: '#2E7D32',
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            ⚡ Actions
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={handleMarkAllPresent}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1.5,
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                ✅ Mark All Present
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleMarkAllAbsent}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1.5,
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                ❌ Mark All Absent
                            </Button>
                            {selectedCourse?.name && selectedSlot && (presentStudents.length || absentStudents.length || leaveStudents.length) ? (
                                 <Button 
                                     variant="contained" 
                                     color="success"
                                     onClick={markAttendance}
                                     disabled={markingAttendance}
                                     startIcon={markingAttendance ? <CircularProgress size={20} /> : <DoneIcon/>}
                                     sx={{
                                         borderRadius: 2,
                                         textTransform: 'none',
                                         fontWeight: 600,
                                         px: 3,
                                         py: 1.5,
                                         backgroundColor: '#4CAF50',
                                         color: '#fff',
                                         '&:hover': {
                                             backgroundColor: '#2E7D32',
                                             transform: 'translateY(-1px)',
                                             boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                         },
                                         '&:disabled': {
                                             backgroundColor: 'rgba(76, 175, 80, 0.5)',
                                             color: 'rgba(255,255,255,0.7)',
                                         },
                                         transition: 'all 0.3s ease',
                                     }}
                                 >
                                     {markingAttendance ? 'Marking...' : '📝 Save Changes'}
                                 </Button>
                             ) : null}
                        </Box>
                    </Paper>
                )}

                {/* Attendance Table Card */}
                {selectedCampus && selectedDate && getAvailableSlots().length > 0 && (
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                            border: '1px solid rgba(76, 175, 80, 0.1)'
                        }}
                    >
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600, 
                            color: '#2E7D32',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        👥 Student Attendance
                    </Typography>
                                         <DataGrid
                         apiRef={attendanceGridApiRef}
                         slots={{toolbar: GridToolbar}}
                         rows={tableData}
                         columns={columns}
                         pageSize={10}
                         checkboxSelection={false}
                         disableSelectionOnClick
                         getRowId={(row) => row.id}
                         initialState={{
                             filter: {
                                 filterModel: {
                                     items: [
                                         {
                                             field: 'status',
                                             operator: 'not',
                                             value: 'InActive',
                                         },
                                     ],
                                 },
                             },
                         }}
                         sx={{
                             border: 'none',
                             height: 400,
                             '& .MuiDataGrid-root': {
                                 border: 'none',
                             },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8f9fa',
                                    borderBottom: '2px solid #4CAF50',
                                },
                         }}
                     />
                </Paper>
                )}

                {/* Subject-Wise Attendance */}
                {selectedStudent && subjectWiseAttendance.length > 0 && (
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                            border: '1px solid rgba(76, 175, 80, 0.1)'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 600, 
                                color: '#2E7D32',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            📊 Subject Wise Attendance
                        </Typography>
                                                 <DataGrid
                             rows={subjectWiseAttendance}
                             slots={{toolbar: GridToolbar}}
                             columns={attendanceColumns}
                             pageSize={10}
                             disableSelectionOnClick
                             getRowId={(row) => row.subject}
                             sx={{
                                 border: 'none',
                                 height: 300,
                                 '& .MuiDataGrid-root': {
                                     border: 'none',
                                 },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8f9fa',
                                    borderBottom: '2px solid #4CAF50',
                                },
                             }}
                         />
                    </Paper>
                )}

                {/* Session Data */}
                {selectedStudent && sessionData.length > 0 && (
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                            border: '1px solid rgba(76, 175, 80, 0.1)'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 600, 
                                color: '#2E7D32',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            📈 All Attendance Records
                        </Typography>
                                                 <DataGrid
                             rows={sessionData}
                             columns={sessionColumns}
                             slots={{toolbar: GridToolbar}}
                             pageSize={2000}
                             disableSelectionOnClick
                             getRowId={(row) => `${row.tableId}`}
                             sx={{
                                 border: 'none',
                                 height: 400,
                                 '& .MuiDataGrid-root': {
                                     border: 'none',
                                 },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8f9fa',
                                    borderBottom: '2px solid #4CAF50',
                                },
                             }}
                         />
                    </Paper>
                )}
            </Box>

            {/* Student Detail Dialog */}
            <Dialog
                open={studentDetailDialog}
                onClose={() => setStudentDetailDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    bgcolor: '#4CAF50', 
                    color: 'white', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton 
                            onClick={() => handleNavigateStudent('prev')}
                            sx={{ 
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                            }}
                            size="small"
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Student Details
                            </Typography>
                            {selectedStudentDetail && (
                                <>
                                    <Chip 
                                        label={selectedStudentDetail.id} 
                                        sx={{ 
                                            bgcolor: 'rgba(255,255,255,0.2)', 
                                            color: 'white',
                                            fontWeight: 600
                                        }} 
                                    />
                                    <Chip 
                                        label={`${tableData.findIndex(s => s.id === selectedStudentDetail.id) + 1} / ${tableData.length}`} 
                                        sx={{ 
                                            bgcolor: 'rgba(255,255,255,0.3)', 
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.85rem'
                                        }} 
                                        size="small"
                                    />
                                </>
                            )}
                        </Box>
                        
                        <IconButton 
                            onClick={() => handleNavigateStudent('next')}
                            sx={{ 
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                            }}
                            size="small"
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AssignmentIcon />}
                            onClick={handlePrintStudentReport}
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            Print
                        </Button>
                        <IconButton 
                            onClick={() => setStudentDetailDialog(false)}
                            sx={{ color: 'white' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    {selectedStudentDetail && (
                        <Box>
                            {/* Basic Profile Section */}
                            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2E7D32' }}>
                                    📋 Profile Information
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Name</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {selectedStudentDetail.name}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Gender</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {selectedStudentDetail.gender}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Campus</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {selectedStudentDetail.campusName || selectedStudentDetail.campusCode || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Status</Typography>
                                        <Chip
                                            label={getStudentStatusLabel(selectedStudentDetail.status)}
                                            color={isActiveLikeStatus(selectedStudentDetail.status) ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Subject-Wise Attendance */}
                            <Paper elevation={2} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                                        📊 Subject-Wise Attendance
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleDownloadAttendanceSummary}
                                            startIcon={<DownloadIcon />}
                                            sx={{
                                                borderColor: '#4CAF50',
                                                color: '#4CAF50',
                                                '&:hover': { borderColor: '#45a049', bgcolor: '#e8f5e9' },
                                                textTransform: 'none',
                                                fontWeight: 500
                                            }}
                                        >
                                            Download
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleViewAllAttendance}
                                            size="small"
                                        >
                                            View All Attendance
                                        </Button>
                                    </Box>
                                </Box>
                                
                                {loadingAttendance ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Box>
                                        {/* Subject-Wise Attendance Details */}
                                        <Collapse in={attendanceExpanded}>
                                            <Box sx={{ mt: 3 }}>
                                                {loadingAttendance ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                                        <CircularProgress />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        {/* Subject-Wise Attendance */}
                                                        {selectedStudentDetail.subjectWiseAttendance?.length > 0 && (
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                                                    Subject-Wise Attendance
                                                                </Typography>
                                                                <TableContainer component={Paper} variant="outlined">
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                                                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                                                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Present</TableCell>
                                                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Absent</TableCell>
                                                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Leaves</TableCell>
                                                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Conducted</TableCell>
                                                                                <TableCell align="center" sx={{ fontWeight: 600 }}>Total Planned</TableCell>
                                                                                <TableCell align="center" sx={{ fontWeight: 600 }}>%</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {selectedStudentDetail.subjectWiseAttendance.map((row) => (
                                                                                <TableRow key={row.subject}>
                                                                                    <TableCell>{row.subject}</TableCell>
                                                                                    <TableCell align="center">{row.present}</TableCell>
                                                                                    <TableCell align="center">{row.absent}</TableCell>
                                                                                    <TableCell align="center">{row.leaves}</TableCell>
                                                                                    <TableCell align="center">{row.conducted}</TableCell>
                                                                                    <TableCell align="center">{row.totalPlanned}</TableCell>
                                                                                    <TableCell align="center">
                                                                                        <Chip 
                                                                                            label={`${row.percentage}%`}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                bgcolor: parseFloat(row.percentage) > ATTENDANCE_PCT_GOOD_ABOVE ? '#4CAF50' : 
                                                                                                         parseFloat(row.percentage) >= ATTENDANCE_PCT_WARN_MIN ? '#FF9800' : '#F44336',
                                                                                                color: 'white',
                                                                                                fontWeight: 600
                                                                                            }}
                                                                                        />
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </Box>
                                                        )}

                                                        {/* Session History */}
                                                        {sessionData?.length > 0 && (
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                                                    Recent Sessions (Last 5)
                                                                </Typography>
                                                                <TableContainer component={Paper} variant="outlined">
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                                                <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                                                                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                                                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                                                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {sessionData.slice(0, 5).map((session, index) => (
                                                                                <TableRow key={index}>
                                                                                    <TableCell>{session.courseName}</TableCell>
                                                                                    <TableCell>
                                                                                        {session.startTime}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Chip
                                                                                            label={session.status}
                                                                                            size="small"
                                                                                            color={
                                                                                                session.status === 'Present' ? 'success' :
                                                                                                session.status === 'Absent' ? 'error' : 'warning'
                                                                                            }
                                                                                        />
                                                                                    </TableCell>
                                                                                    <TableCell>{session.reason || '-'}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </Box>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* All Attendance Dialog */}
            <Dialog
                open={allAttendanceDialog}
                onClose={() => {
                    setAllAttendanceDialog(false);
                    setSelectedAttendanceRows([]);
                }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    bgcolor: '#4CAF50', 
                    color: 'white', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Complete Attendance History
                        </Typography>
                        {selectedStudentDetail && (
                            <>
                                <Chip 
                                    label={selectedStudentDetail.id} 
                                    sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.2)', 
                                        color: 'white',
                                        fontWeight: 600
                                    }} 
                                />
                                <Chip 
                                    label={selectedStudentDetail.name} 
                                    sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.2)', 
                                        color: 'white',
                                        fontWeight: 600
                                    }} 
                                />
                            </>
                        )}
                    </Box>
                    <IconButton 
                        onClick={() => {
                            setAllAttendanceDialog(false);
                            setSelectedAttendanceRows([]);
                        }}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ mt: 2, p: 3 }}>
                    {loadingAllAttendance ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress size={60} />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedAttendanceRows.length > 0 
                                        ? `${selectedAttendanceRows.length} record(s) selected` 
                                        : 'Select records to print or print all'}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handlePrintAttendance}
                                    startIcon={<AssignmentIcon />}
                                    sx={{
                                        bgcolor: '#4CAF50',
                                        '&:hover': { bgcolor: '#45a049' }
                                    }}
                                >
                                    Print Report
                                </Button>
                            </Box>
                            <DataGrid
                                rows={allAttendanceData}
                                columns={[
                                {
                                    field: 'courseName',
                                    headerName: 'Course',
                                    width: 200,
                                    flex: 1
                                },
                                {
                                    field: 'startTime',
                                    headerName: 'Date & Time',
                                    width: 180
                                },
                                {
                                    field: 'topic',
                                    headerName: 'Topic',
                                    width: 200,
                                    flex: 1,
                                    renderCell: (params) => {
                                        const value = params.value || '';
                                        const urdu = isUrduText(value);
                                        return (
                                            <Typography 
                                                className={urdu ? 'urdu' : ''} 
                                                variant="body2"
                                                sx={{
                                                    ...getUrduSxProps(value),
                                                    direction: 'ltr',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {value || '-'}
                                            </Typography>
                                        );
                                    }
                                },
                                {
                                    field: 'status',
                                    headerName: 'Status',
                                    width: 120,
                                    renderCell: (params) => (
                                        <Chip
                                            label={params.value}
                                            size="small"
                                            color={
                                                params.value === 'Present' ? 'success' :
                                                params.value === 'Absent' ? 'error' : 'warning'
                                            }
                                        />
                                    )
                                },
                                {
                                    field: 'reason',
                                    headerName: 'Leave Reason',
                                    width: 200,
                                    flex: 1,
                                    renderCell: (params) => params.value || '-'
                                },
                                {
                                    field: 'comments',
                                    headerName: 'Comments',
                                    width: 200,
                                    flex: 1,
                                    renderCell: (params) => {
                                        const value = params.value || '';
                                        const urdu = isUrduText(value);
                                        return (
                                            <Typography 
                                                className={urdu ? 'urdu' : ''} 
                                                variant="body2"
                                                sx={{
                                                    ...getUrduSxProps(value),
                                                    direction: 'ltr',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {value || '-'}
                                            </Typography>
                                        );
                                    }
                                }
                            ]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10, page: 0 },
                                },
                            }}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            checkboxSelection
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={(newSelection) => {
                                setSelectedAttendanceRows(newSelection);
                            }}
                            rowSelectionModel={selectedAttendanceRows}
                            slots={{ toolbar: GridToolbar }}
                            sx={{
                                height: 600,
                                border: 'none',
                                '& .MuiDataGrid-root': {
                                    border: 'none',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8f9fa',
                                    borderBottom: '2px solid #4CAF50',
                                    fontWeight: 600
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: '2px solid #4CAF50',
                                }
                            }}
                        />
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar for notifications */}
            {/* Date Change Dialog */}
            <Dialog
                open={dateChangeDialog}
                onClose={() => {
                    setDateChangeDialog(false);
                    setAttendanceToChangeDate(null);
                    setNewDate(null);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3
                    }
                }}
            >
                <DialogTitle sx={{ 
                    bgcolor: '#ff9800', 
                    color: 'white', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 2
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        📅 Change Attendance Date
                    </Typography>
                    <IconButton
                        onClick={() => {
                            setDateChangeDialog(false);
                            setAttendanceToChangeDate(null);
                            setNewDate(null);
                        }}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {attendanceToChangeDate && (() => {
                        const startTimeMatch = attendanceToChangeDate.startTime?.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                        const endTimeMatch = attendanceToChangeDate.endTime?.match(/^(\d{4}-\d{2}-\d{2})-(\d{2}):(\d{2})$/);
                        const startTime = startTimeMatch ? `${startTimeMatch[2]}:${startTimeMatch[3]}` : 'N/A';
                        const endTime = endTimeMatch ? `${endTimeMatch[2]}:${endTimeMatch[3]}` : 'N/A';
                        const currentDate = startTimeMatch ? startTimeMatch[1] : 'N/A';
                        
                        return (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Current Record:
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Course:</strong> {attendanceToChangeDate.courseName}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Time:</strong> {startTime} - {endTime}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Current Date:</strong> {currentDate}
                                    </Typography>
                                </Alert>
                            
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#ff9800' }}>
                                    Select New Date
                                </Typography>
                                <DatePicker
                                    value={newDate}
                                    disableFuture={true}
                                    onChange={(newValue) => setNewDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="New Date"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    '&:hover fieldset': {
                                                        borderColor: '#ff9800',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#f57c00',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                                {newDate && (() => {
                                    const dateCheck = checkDateChangeAllowed(newDate);
                                    
                                    return (
                                        <Box sx={{ mt: 2 }}>
                                            {dateCheck.allowed ? (
                                                <Alert severity="success" sx={{ borderRadius: 2 }}>
                                                    ✅ Date {newDate.format('dddd, DD/MM/YYYY')} is available. The system will automatically map this record to an available slot.
                                                </Alert>
                                            ) : (
                                                <Alert severity="error" sx={{ borderRadius: 2 }}>
                                                    ❌ {dateCheck.reason}
                                                </Alert>
                                            )}
                                        </Box>
                                    );
                                })()}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setDateChangeDialog(false);
                                        setAttendanceToChangeDate(null);
                                        setNewDate(null);
                                    }}
                                    disabled={updatingDate}
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={handleUpdateDate}
                                    disabled={updatingDate || !newDate}
                                    startIcon={updatingDate ? <CircularProgress size={20} /> : null}
                                    sx={{ 
                                        borderRadius: 2, 
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3
                                    }}
                                >
                                    {updatingDate ? 'Updating...' : 'Update Date'}
                                </Button>
                            </Box>
                        </Box>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </LocalizationProvider>
    );
};

export default CourseSelectorWithTable;
